import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  BookOpen, 
  UserPlus, 
  Users, 
  ClipboardList, 
  FileText, 
  CreditCard,
  BarChart,
  History,
  Calendar,
  Settings,
  CreditCard as SubscriptionIcon,
  CheckCircle2,
  ChevronRight,
  Search,
  UserCog,
  Stethoscope,
  Receipt,
  Clock,
  Mail,
  Shield,
  HelpCircle
} from 'lucide-react';

export default function UserGuide() {
  const [activeSection, setActiveSection] = useState<string>('getting-started');

  const guideSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      steps: [
        {
          title: 'Creating Your Hospital Account',
          steps: [
            'Visit the AM CARE website and navigate to the signup page',
            'Click "Get Started" or "Sign Up" button',
            'Enter your Hospital Name, Email, Phone Number, and Address',
            'Create your Admin Account with your name, email, and password',
            'Click "Complete Registration" and wait for confirmation'
          ]
        },
        {
          title: 'Email Confirmation',
          steps: [
            'Check your email inbox for confirmation email from AM CARE',
            'Click the confirmation link in the email',
            'Wait for "Email Confirmed Successfully" message',
            'You will be automatically redirected to login page'
          ]
        },
        {
          title: 'First Login',
          steps: [
            'Go to the login page',
            'Enter your admin email address and password',
            'Click "Login" to access your dashboard',
            'Welcome to AM CARE!'
          ]
        }
      ]
    },
    {
      id: 'admin-setup',
      title: 'Admin Account Setup',
      icon: Settings,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      steps: [
        {
          title: 'Accessing Settings',
          steps: [
            'Click "Settings" in the left sidebar menu',
            'You will see multiple tabs: Profile, Users, Services, Notifications, Integrations, System'
          ]
        },
        {
          title: 'Hospital Profile Configuration',
          steps: [
            'Go to Profile tab in Settings',
            'Update Hospital Name, Email, Phone, and Address',
            'Click "Save Changes" to update information',
            'Upload your hospital logo (optional) - Recommended size: 200x200 pixels',
            'Customize wallpaper for your dashboard background'
          ]
        }
      ]
    },
    {
      id: 'user-management',
      title: 'User Management',
      icon: UserCog,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      steps: [
        {
          title: 'Creating Doctor Accounts',
          steps: [
            'Navigate to Settings → Users tab or click "Users" from sidebar',
            'Click the "+ Add User" button',
            'Fill in Doctor Information: Name, Email, Role (select "Doctor"), Specialty, Registration Number',
            'Create a password and confirm it',
            'Click "Create User" - Doctor will receive email with login credentials'
          ]
        },
        {
          title: 'Creating Staff Accounts',
          steps: [
            'Go to Settings → Users tab',
            'Click "+ Add User" button',
            'Fill Staff Information: Name, Email, Role (select "Staff"), Password',
            'Click "Create User" - Staff member will receive login credentials'
          ]
        },
        {
          title: 'Managing User Roles',
          steps: [
            'View all users in Settings → Users tab',
            'Find the user and click the dropdown in "Role" column',
            'Select new role: Admin, Doctor, or Staff',
            'Changes are saved automatically'
          ]
        }
      ]
    },
    {
      id: 'patient-registration',
      title: 'Patient Registration',
      icon: UserPlus,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      steps: [
        {
          title: 'Registering New Patients',
          steps: [
            'Click "Registration" in sidebar menu or Dashboard → "Register Patient"',
            'Fill Patient Information: Name, Age, Gender, Contact Number, Reason for Visit',
            'Select assigned doctor (optional) and priority level',
            'System automatically checks if patient exists by contact number',
            'Click "Register Patient" - Patient is added to queue automatically'
          ]
        },
        {
          title: 'Searching Existing Patients',
          steps: [
            'Go to Registration page',
            'Type patient name or contact number in search bar',
            'Matching patients appear below',
            'Click on patient to view history and register new visit'
          ]
        }
      ]
    },
    {
      id: 'queue-management',
      title: 'Queue Management',
      icon: ClipboardList,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      steps: [
        {
          title: 'Viewing the Queue',
          steps: [
            'Click "Queue" in sidebar menu',
            'See all patients currently waiting in real-time',
            'View patient name, age, reason, assigned doctor, wait time, and priority',
            'Filter by doctor, status, or priority'
          ]
        },
        {
          title: 'Assigning Patients to Doctors',
          steps: [
            'Click on patient card in queue',
            'Select doctor from dropdown',
            'Click "Assign" - Doctor sees patient in their dashboard'
          ]
        },
        {
          title: 'Managing Patient Priority',
          steps: [
            'Click on patient in queue',
            'Click priority badge and select: Normal (blue), Urgent (orange), or Emergency (red)',
            'Priority affects queue order - Emergency patients appear at top'
          ]
        }
      ]
    },
    {
      id: 'prescriptions',
      title: 'E-Prescription System',
      icon: FileText,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      steps: [
        {
          title: 'Creating Prescriptions',
          steps: [
            'Go to patient details page and click "Create Prescription"',
            'Enter Chief Complaint and duration',
            'Add Medications: Name, Dosage, Frequency, Duration, Instructions',
            'Add Lab Tests (optional)',
            'Enter Clinical Diagnosis and notes',
            'Click "Save Prescription"'
          ]
        },
        {
          title: 'Using Medication Templates',
          steps: [
            'While creating prescription, click "Templates" button',
            'Browse available templates and click to use',
            'Template medications load automatically - edit as needed',
            'Save custom templates for future use'
          ]
        },
        {
          title: 'Printing Prescriptions',
          steps: [
            'Access prescription from patient details or History',
            'Click "Print Prescription" button',
            'Select printer and click "Print"',
            'Professional format with hospital logo and doctor signature space'
          ]
        }
      ]
    },
    {
      id: 'billing',
      title: 'Billing & Payments',
      icon: Receipt,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      steps: [
        {
          title: 'Creating Bills',
          steps: [
            'Go to patient details page and click "Create Bill"',
            'Click "Add Service" and select service from list',
            'Enter quantity and price (if different)',
            'Add all services patient received',
            'Apply discount (optional)',
            'Click "Generate Bill" - Bill number is assigned'
          ]
        },
        {
          title: 'Recording Payments',
          steps: [
            'Go to patient details and click "Record Payment"',
            'Enter payment amount and select payment method (Cash, Card, UPI, etc.)',
            'Enter transaction reference number (optional)',
            'Click "Record Payment" - Receipt is generated automatically'
          ]
        },
        {
          title: 'Managing Services',
          steps: [
            'Go to Settings → Services tab',
            'Click "Add Service" to create new service',
            'Enter service name, default price, and description',
            'Edit or delete services as needed'
          ]
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      icon: BarChart,
      color: 'from-cyan-500 to-cyan-600',
      bgColor: 'bg-cyan-50',
      steps: [
        {
          title: 'Accessing Analytics Dashboard',
          steps: [
            'Click "Analytics" in sidebar menu (Admin only)',
            'View summary cards: Total Revenue, Total Patients, Today\'s Revenue',
            'Select date range for analysis',
            'Explore multiple analytics tabs'
          ]
        },
        {
          title: 'Revenue Analytics',
          steps: [
            'Click "Revenue" tab',
            'View Revenue by Doctor - See which doctors generate most revenue',
            'View Revenue by Date - Daily revenue chart and trends',
            'View Revenue by Payment Method - Cash vs Card vs UPI distribution',
            'View Revenue by Service - Service-wise breakdown',
            'Monthly Revenue Trends - Month-over-month comparison'
          ]
        },
        {
          title: 'Patient Analytics',
          steps: [
            'Click "Patients" tab',
            'View Patient Count by Doctor',
            'See Appointments by Date - Identify busy days',
            'View Weekly Distribution - Plan staffing accordingly',
            'Advanced metrics: Retention rate, Returning patients, Demographics'
          ]
        },
        {
          title: 'Exporting Reports',
          steps: [
            'Select data type: Revenue, Patient, or Appointment data',
            'Choose export format: CSV (Excel) or PDF',
            'Click "Export" - File downloads automatically'
          ]
        }
      ]
    },
    {
      id: 'history',
      title: 'Patient History',
      icon: History,
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50',
      steps: [
        {
          title: 'Viewing Complete History',
          steps: [
            'Click "History" in sidebar menu',
            'All patients appear in list',
            'Use search bar to find specific patient',
            'Click on patient name to view complete history'
          ]
        },
        {
          title: 'Prescription History',
          steps: [
            'In patient history, scroll to "Prescriptions" section',
            'All prescriptions listed chronologically',
            'Click on prescription to view full details',
            'Print old prescriptions for reference'
          ]
        },
        {
          title: 'Billing History',
          steps: [
            'Scroll to "Bills" section in patient history',
            'View all bills chronologically',
            'Click on bill to see services, amounts, payment status',
            'View individual payment details and receipts'
          ]
        }
      ]
    },
    {
      id: 'followups',
      title: 'Follow-up Management',
      icon: Calendar,
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-50',
      steps: [
        {
          title: 'Scheduling Follow-ups',
          steps: [
            'Go to patient details page and click "Schedule Follow-up"',
            'Select follow-up date and time (optional)',
            'Enter reason for follow-up and notes',
            'Select status: Scheduled, Completed, or Cancelled',
            'Click "Schedule Follow-up"'
          ]
        },
        {
          title: 'Viewing Follow-up List',
          steps: [
            'Click "Follow-ups" in sidebar menu',
            'View all follow-ups',
            'Filter by status, date range, or patient',
            'Click on follow-up to see details'
          ]
        },
        {
          title: 'Managing Follow-ups',
          steps: [
            'Update status: Click on follow-up and change status',
            'Edit follow-up: Update date, time, or notes',
            'Complete follow-up: Change status to "Completed" when patient arrives',
            'Cancel follow-up: Change status to "Cancelled" with reason'
          ]
        }
      ]
    },
    {
      id: 'subscription',
      title: 'Subscription Management',
      icon: SubscriptionIcon,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      steps: [
        {
          title: 'Viewing Subscription Status',
          steps: [
            'Click "Subscriptions" in sidebar menu',
            'View current plan: Name, Start date, End date, Status',
            'See all included features',
            'All plans include all features - no restrictions'
          ]
        },
        {
          title: 'Upgrading Plans',
          steps: [
            'View available plans: 3 Months, 6 Months, and 12 Months options',
            'Click on desired plan and view details',
            'Click "Subscribe" and complete payment via Razorpay',
            'Subscription activates automatically after payment'
          ]
        },
        {
          title: 'Payment History',
          steps: [
            'Go to Subscription page',
            'Scroll to "Payment History" section',
            'View all payments with dates, payment methods, and transaction details',
            'Download receipts as PDF for accounting'
          ]
        }
      ]
    }
  ];

  const quickTips = [
    { icon: Search, text: 'Use search bar to quickly find patients' },
    { icon: Clock, text: 'Queue updates in real-time automatically' },
    { icon: FileText, text: 'Save medication templates for faster prescriptions' },
    { icon: BarChart, text: 'Check analytics regularly to track growth' },
    { icon: Calendar, text: 'Schedule follow-ups to improve patient care' },
    { icon: Shield, text: 'All data is secure and isolated per hospital' }
  ];

  const rolePermissions = [
    {
      role: 'Admin',
      icon: Shield,
      color: 'text-blue-600',
      permissions: [
        'Full access to all features',
        'User management',
        'Settings access',
        'Analytics access',
        'Subscription management'
      ]
    },
    {
      role: 'Doctor',
      icon: Stethoscope,
      color: 'text-green-600',
      permissions: [
        'Doctor dashboard',
        'View assigned patients',
        'Create prescriptions',
        'View patient history',
        'Access templates'
      ]
    },
    {
      role: 'Staff',
      icon: Users,
      color: 'text-orange-600',
      permissions: [
        'Patient registration',
        'Queue management',
        'View patient history',
        'Schedule follow-ups',
        'Cannot access analytics or settings'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">User Guide</h1>
                  <p className="text-sm text-gray-600">Complete step-by-step instructions</p>
                </div>
              </div>
            </div>
            <Link to="/signup">
              <Button className="gap-2">
                Get Started
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">Table of Contents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {guideSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                        activeSection === section.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${activeSection === section.id ? 'text-white' : section.color.replace('from-', 'text-').replace('to-', '').split(' ')[0]}`} />
                      <span className="text-sm font-medium">{section.title}</span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Welcome Section */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="h-8 w-8" />
                  <CardTitle className="text-3xl">Welcome to AM CARE</CardTitle>
                </div>
                <CardDescription className="text-blue-100 text-base">
                  Your complete guide to using AM CARE Hospital Management System. Follow these step-by-step instructions to maximize your clinic's efficiency.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-2xl font-bold">12+</div>
                    <div className="text-sm text-blue-100">Feature Sections</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-2xl font-bold">50+</div>
                    <div className="text-sm text-blue-100">Step-by-Step Guides</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-2xl font-bold">100%</div>
                    <div className="text-sm text-blue-100">Feature Coverage</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guide Sections */}
            {guideSections.map((section, sectionIndex) => {
              const Icon = section.icon;
              return (
                <Card
                  key={section.id}
                  id={section.id}
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <CardHeader className={`bg-gradient-to-r ${section.color} text-white rounded-t-lg`}>
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">{section.title}</CardTitle>
                        <CardDescription className="text-white/90">
                          Section {sectionIndex + 1} of {guideSections.length}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-6">
                      {section.steps.map((stepGroup, stepIndex) => (
                        <div key={stepIndex} className="space-y-3">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${section.color} text-white font-bold min-w-[2rem] h-8 flex items-center justify-center`}>
                              {stepIndex + 1}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">{stepGroup.title}</h3>
                              <ul className="space-y-2">
                                {stepGroup.steps.map((step, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-gray-700">
                                    <CheckCircle2 className={`h-5 w-5 ${section.color.replace('from-', 'text-').replace('to-', '').split(' ')[0]} mt-0.5 flex-shrink-0`} />
                                    <span className="text-sm leading-relaxed">{step}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          {stepIndex < section.steps.length - 1 && (
                            <div className="border-t border-gray-200 my-4"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Quick Tips */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-blue-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-600" />
                  Quick Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {quickTips.map((tip, idx) => {
                    const TipIcon = tip.icon;
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                        <TipIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{tip.text}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Role Permissions */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Role Permissions</CardTitle>
                <CardDescription>Understand what each role can access</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {rolePermissions.map((role, idx) => {
                    const RoleIcon = role.icon;
                    return (
                      <Card key={idx} className="border-2">
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <RoleIcon className={`h-5 w-5 ${role.color}`} />
                            <CardTitle className="text-lg">{role.role}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {role.permissions.map((permission, pIdx) => (
                              <li key={pIdx} className="flex items-start gap-2 text-sm text-gray-700">
                                <CheckCircle2 className={`h-4 w-4 ${role.color} mt-0.5 flex-shrink-0`} />
                                <span>{permission}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Support Section */}
            <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Mail className="h-6 w-6" />
                  Need Help?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-blue-100">
                    If you need additional assistance or have questions, our support team is here to help!
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="font-semibold mb-2">Email Support</div>
                      <a href="mailto:support@amcares.in" className="text-blue-100 hover:text-white underline">
                        support@amcares.in
                      </a>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <div className="font-semibold mb-2">Phone Support</div>
                      <a href="tel:+917799647888" className="text-blue-100 hover:text-white underline">
                        +91 7799647888
                      </a>
                    </div>
                  </div>
                  <div className="pt-4">
                    <Link to="/contact-us">
                      <Button variant="secondary" className="w-full">
                        Contact Us
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

